'use client'

import { useState, useEffect, useRef } from 'react'
import { Loader2, Search, User } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { searchUsers, inviteUser } from '@/lib/services/teamService'
import { useDebounce } from '@/lib/hooks/use-debounce'
import type { UserSearchResult } from '@/lib/types/team'

// ============================================================================
// TYPES
// ============================================================================

interface InviteUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingMemberIds: number[]
  pendingInvitationUserIds: number[]
  // Mode: immediate calls API, deferred returns data via callback
  mode?: 'immediate' | 'deferred'
  // For immediate mode
  productSlug?: string
  onSuccess?: () => void
  // For deferred mode
  onUserSelected?: (user: UserSearchResult, role: string) => void
}

// ============================================================================
// COMPONENT
// ============================================================================

export function InviteUserDialog({
  open,
  onOpenChange,
  existingMemberIds,
  pendingInvitationUserIds,
  mode = 'immediate',
  productSlug,
  onSuccess,
  onUserSelected,
}: InviteUserDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null)
  const [role, setRole] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounce(searchQuery, 300)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSearchResults([])
      setSelectedUser(null)
      setRole('')
      setError(null)
      setShowResults(false)
    }
  }, [open])

  // Search users when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      performSearch(debouncedQuery)
    } else {
      setSearchResults([])
      setShowResults(false)
    }
  }, [debouncedQuery])

  // Close results dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const performSearch = async (query: string) => {
    setIsSearching(true)
    const result = await searchUsers(query)
    if (result.success && result.data) {
      // Filter out existing members and pending invitations
      const excludedIds = [...existingMemberIds, ...pendingInvitationUserIds]
      const filtered = result.data.users.filter(
        user => !excludedIds.includes(user.id)
      )
      setSearchResults(filtered)
      setShowResults(true)
    }
    setIsSearching(false)
  }

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user)
    setSearchQuery(user.name)
    setShowResults(false)
    setError(null)
  }

  const handleClearSelection = () => {
    setSelectedUser(null)
    setSearchQuery('')
    inputRef.current?.focus()
  }

  const handleInvite = async () => {
    if (!selectedUser) {
      setError('Выберите пользователя')
      return
    }

    if (!role.trim()) {
      setError('Укажите роль')
      return
    }

    if (role.length > 30) {
      setError('Роль не может быть длиннее 30 символов')
      return
    }

    // Deferred mode: just return the data via callback
    if (mode === 'deferred') {
      onUserSelected?.(selectedUser, role.trim())
      onOpenChange(false)
      return
    }

    // Immediate mode: call API
    if (!productSlug) {
      setError('Product slug is required for immediate mode')
      return
    }

    setIsInviting(true)
    setError(null)

    const result = await inviteUser(productSlug, selectedUser.id, role.trim())

    if (result.success) {
      onSuccess?.()
      onOpenChange(false)
    } else {
      setError(result.error?.message || 'Не удалось отправить приглашение')
    }

    setIsInviting(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Пригласить участника</DialogTitle>
          <DialogDescription>
            Найдите пользователя и пригласите его в команду
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Search */}
          <div className="space-y-2">
            <Label htmlFor="user-search">Пользователь</Label>
            <div className="relative">
              {selectedUser ? (
                <div className="flex items-center gap-2 p-2 rounded-md border border-border bg-muted/50">
                  <Avatar className="h-8 w-8">
                    {selectedUser.avatarUrl && (
                      <AvatarImage src={selectedUser.avatarUrl} alt={selectedUser.name} />
                    )}
                    <AvatarFallback>
                      {selectedUser.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{selectedUser.name}</div>
                    {selectedUser.username && (
                      <div className="text-xs text-foreground-tertiary truncate">
                        @{selectedUser.username}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSelection}
                  >
                    Изменить
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-tertiary" />
                    <Input
                      ref={inputRef}
                      id="user-search"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowResults(true)}
                      placeholder="Поиск по имени или username..."
                      className="pl-9"
                      autoComplete="off"
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-foreground-tertiary" />
                    )}
                  </div>

                  {/* Search Results Dropdown */}
                  {showResults && (
                    <div
                      ref={resultsRef}
                      className="absolute z-10 w-full mt-1 py-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
                    >
                      {searchResults.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-foreground-tertiary">
                          {searchQuery.length < 2
                            ? 'Введите минимум 2 символа'
                            : 'Пользователи не найдены'}
                        </div>
                      ) : (
                        searchResults.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
                            onClick={() => handleSelectUser(user)}
                          >
                            <Avatar className="h-8 w-8">
                              {user.avatarUrl && (
                                <AvatarImage src={user.avatarUrl} alt={user.name} />
                              )}
                              <AvatarFallback>
                                {user.name.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{user.name}</div>
                              {user.username && (
                                <div className="text-xs text-foreground-tertiary truncate">
                                  @{user.username}
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Role Input */}
          <div className="space-y-2">
            <Label htmlFor="role">Роль в команде</Label>
            <Input
              id="role"
              value={role}
              onChange={e => setRole(e.target.value)}
              placeholder="Например: Разработчик, Дизайнер, Маркетолог..."
              maxLength={30}
            />
            <p className="text-xs text-foreground-tertiary">
              Максимум 30 символов ({role.length}/30)
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-destructive">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={handleInvite} disabled={isInviting || !selectedUser}>
            {isInviting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Отправка...
              </>
            ) : (
              'Пригласить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
